import React, { useState } from "react";
import styles from "../../../../styles/mailCenter/emailHistoryTab.module.scss";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { isAuth } from "../../../../pages/auth/ProtectedRoute";
import TemplateCardSkeleton from "../components/TemplateCardSkeleton";

type CampaignStatus =
  | "Sent"
  | "Scheduled"
  | "Draft"
  | "Failed"
  | "sent"
  | "scheduled"
  | "draft"
  | "failed";
type StandardStatus = "Sent" | "Scheduled" | "Draft" | "Failed";
type FilterOption = "All" | StandardStatus;

interface Campaign {
  id: number;
  audience?: string;
  date?: string;
  sentCount: number | null;
  status: CampaignStatus;
  title?: string;
  preview?: string;
  name?: any;
  template_id?: any;
  subject: string;
  body: string;
  from_name: string;
  from_email: string;
  content_type: string;
  recipient_type: string;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  opened_count: number;
  clicked_count: number;
  scheduled_at: string;
  sent_at: string;
  created_by: number;
  created_at: string;
  updated_at: string;
  deleted_at: string;
  template?: any;
}

 const formatTimestamp = (timestamp: string | number | Date) => {
  const date = new Date(timestamp);

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const FILTER_OPTIONS: FilterOption[] = [
  "All",
  "Sent",
  "Scheduled",
  "Draft",
  "Failed",
];

const normalizeStatus = (status: CampaignStatus): StandardStatus => {
  const normalized = status.toLowerCase();

  if (normalized === "sent") return "Sent";
  if (normalized === "scheduled") return "Scheduled";
  if (normalized === "draft") return "Draft";
  return "Failed";
};

const MOCK_CAMPAIGNS: Campaign[] = [
  {
    id: 4,
    subject: "Welcome to Kominiti Platform",
    body: "Hi there! Welcome to our mentorship platform...",
    recipient_type: "all",
    date: "Mar 22, 2026, 10:30 AM",
    status: "draft",
    sentCount: 3,
    from_name: "Canny",
    from_email: "canny@kominiti.com",
    content_type: "plain",
    total_recipients: 100,
    sent_count: 95,
    failed_count: 5,
    opened_count: 3,
    clicked_count: 2,
    scheduled_at: "2026-03-22T10:30:00Z",
    sent_at: "2026-03-22T10:30:00Z",
    created_by: 1,
    created_at: "2026-03-22T10:30:00Z",
    updated_at: "2026-03-22T10:30:00Z",
    deleted_at: "2026-03-22T10:30:00Z",
  },
];

const SearchIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const UsersIcon: React.FC = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const CalendarIcon: React.FC = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const SendIcon: React.FC = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const MailIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const CheckCircleIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const XCircleIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const RefreshIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const ClockIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.9987 14.6693C11.6806 14.6693 14.6654 11.6845 14.6654 8.0026C14.6654 4.32071 11.6806 1.33594 7.9987 1.33594C4.3168 1.33594 1.33203 4.32071 1.33203 8.0026C1.33203 11.6845 4.3168 14.6693 7.9987 14.6693Z"
      stroke="#F77F00"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 4V8L10.6667 9.33333"
      stroke="#F77F00"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DraftIcon: React.FC = () => (
  <svg
    width="14"
    height="15"
    viewBox="0 0 14 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.9349 6.60156V5.9349C11.9349 3.4209 11.9349 2.16356 11.1542 1.3829C10.3736 0.602229 9.1149 0.601563 6.60156 0.601563H5.9349C3.42156 0.601563 2.16356 0.601562 1.3829 1.3829C0.602229 2.16423 0.601562 3.4209 0.601562 5.9349V8.60156C0.601562 11.1156 0.601562 12.3729 1.3829 13.1536C2.16423 13.9342 3.4209 13.9349 5.9349 13.9349"
      stroke="#3A3A3A"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.60156 3.9375H8.9349M3.60156 7.27083H8.9349"
      stroke="#3A3A3A"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <path
      d="M7.60156 13.1532V13.9352H8.38423C8.6569 13.9352 8.79356 13.9352 8.91623 13.8845C9.0389 13.8339 9.13556 13.7372 9.32823 13.5445L12.5442 10.3279C12.7262 10.1459 12.8176 10.0545 12.8656 9.95721C12.9582 9.77054 12.9582 9.55054 12.8656 9.36388C12.8169 9.26588 12.7256 9.17521 12.5442 8.99321C12.3616 8.81121 12.2709 8.71988 12.1729 8.67121C12.0807 8.6254 11.9792 8.60156 11.8762 8.60156C11.7733 8.60156 11.6717 8.6254 11.5796 8.67121C11.4816 8.71988 11.3902 8.81121 11.2089 8.99321L7.9929 12.2092C7.79956 12.4019 7.70356 12.4985 7.6529 12.6212C7.60223 12.7439 7.60156 12.8799 7.60156 13.1532Z"
      stroke="#3A3A3A"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const EditIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_11909_43008)">
      <path
        d="M6.0013 4.66406H4.0013C3.64768 4.66406 3.30854 4.80454 3.05849 5.05459C2.80844 5.30464 2.66797 5.64377 2.66797 5.9974V11.9974C2.66797 12.351 2.80844 12.6902 3.05849 12.9402C3.30854 13.1903 3.64768 13.3307 4.0013 13.3307H10.0013C10.3549 13.3307 10.6941 13.1903 10.9441 12.9402C11.1942 12.6902 11.3346 12.351 11.3346 11.9974V9.9974"
        stroke="#4895EF"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 10.0028H8L13.6667 4.33609C13.9319 4.07087 14.0809 3.71116 14.0809 3.33609C14.0809 2.96102 13.9319 2.6013 13.6667 2.33609C13.4014 2.07087 13.0417 1.92188 12.6667 1.92188C12.2916 1.92188 11.9319 2.07087 11.6667 2.33609L6 8.00276V10.0028Z"
        stroke="#4895EF"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.668 3.33594L12.668 5.33594"
        stroke="#4895EF"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_11909_43008">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

interface StatusBadgeProps {
  status: CampaignStatus;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const normalizedStatus = normalizeStatus(status);

  const modifierMap: Record<StandardStatus, string> = {
    Sent: styles["campaign-card__status--sent"],
    Failed: styles["campaign-card__status--failed"],
    Scheduled: styles["campaign-card__status--scheduled"],
    Draft: styles["campaign-card__status--draft"],
  };

  const icon: Record<StandardStatus, React.ReactNode> = {
    Sent: <CheckCircleIcon />,
    Failed: <XCircleIcon />,
    Scheduled: <ClockIcon />,
    Draft: <DraftIcon />,
  };

  return (
    <span
      className={`${styles["campaign-card__status"]} ${modifierMap[normalizedStatus]}`}
    >
      {icon[normalizedStatus]}
      {normalizedStatus}
    </span>
  );
};

interface CampaignCardProps {
  campaign: Campaign;
}

const CampaignCard: React.FC<CampaignCardProps> = ({ campaign }) => {
  const title = campaign.title ?? campaign.subject;
  const preview = campaign.preview ?? campaign.body;
  const normalizedStatus = normalizeStatus(campaign.status);

  return (
    <div className={styles["campaign-card"]}>
      <div className={styles["campaign-card__top"]}>
        <div className={styles["campaign-card__left"]}>
          <div className={styles["campaign-card__icon-wrap"]}>
            <MailIcon />
          </div>
          <div className={styles["campaign-card__info"]}>
            <span className={styles["campaign-card__title"]}>
              {title}
            </span>
            <span className={styles["campaign-card__preview"]}>
              {preview}
            </span>
          </div>
        </div>

        <div>
          {normalizedStatus === "Draft" && (
            <button
              className={`${styles["campaign-card__status"]} ${styles["campaign-card__status--edit"]}`}
            >
              <EditIcon />
              Edit
            </button>
          )}
          <StatusBadge status={campaign.status} />
        </div>
    </div>

    <div className={styles["campaign-card__meta"]}>
      <div className={styles["campaign-card__meta-left"]}>
        <span className={styles["campaign-card__meta-item"]}>
          <span className={styles["campaign-card__meta-icon"]}>
            <UsersIcon />
          </span>
          {campaign.audience ?? "All recipients"}
        </span>
        <span className={styles["campaign-card__meta-item"]}>
          <span className={styles["campaign-card__meta-icon"]}>
            {normalizedStatus === "Scheduled" ? <ClockIcon /> : <CalendarIcon />}
          </span>
          {formatTimestamp(campaign?.created_at)}
        </span>
        {campaign?.status !=='Draft' && campaign?.sent_count !== 0 && (
          <span className={styles["campaign-card__meta-item"]}>
            <span className={styles["campaign-card__meta-icon"]}>
              <SendIcon />
            </span>
            {campaign?.sent_count ? campaign?.sent_count : 0} Sent
          </span>
        )}
      </div>

      {normalizedStatus === "Failed" && (
        <button className={styles["campaign-card__resend-btn"]}>
          <RefreshIcon /> Resend
        </button>
      )}
    </div>
  </div>
  );
};

interface ToolbarProps {
  search: string;
  onSearch: (v: string) => void;
  activeFilter: FilterOption;
  onFilter: (f: FilterOption) => void;
}

const Toolbar: React.FC<ToolbarProps> = ({
  search,
  onSearch,
  activeFilter,
  onFilter,
}) => (
  <div className={styles["history-tab__toolbar"]}>
    <div className={styles["history-tab__search-wrap"]}>
      <span className={styles["history-tab__search-icon"]}>
        <SearchIcon />
      </span>
      <input
        className={styles["history-tab__search-input"]}
        placeholder="Search campaigns by subject..."
        value={search}
        onChange={(e) => onSearch(e.target.value)}
      />
    </div>
    <div className={styles["history-tab__filters"]}>
      {FILTER_OPTIONS.map((f) => (
        <button
          key={f}
          onClick={() => onFilter(f)}
          className={[
            styles["history-tab__filter-btn"],
            activeFilter === f ? styles["history-tab__filter-btn--active"] : "",
          ].join(" ")}
        >
          {f}
        </button>
      ))}
    </div>
  </div>
);


const EmailHistoryTab: React.FC = () => {
  const [search, setSearch] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState<FilterOption>("All");


   const getTemplates = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_HOST_API}mail-center/campaigns`,
        {
          headers: {
            Authorization: `Bearer ${isAuth()}`,
          },
        }
      );
      return response.data;
    };

  const {
      data: campaignsData,
      isLoading,
      error,
      refetch,
    } = useQuery({
      queryKey: ["campaigns"],
      queryFn: getTemplates,
    });


  const filtered: Campaign[] = campaignsData?.data?.data?.filter((c: Campaign) => {
    const matchesSearch = c.subject.toLowerCase().includes(search.toLowerCase());
    const matchesFilter =
      activeFilter === "All" || normalizeStatus(c.status) === activeFilter;
    return matchesSearch && matchesFilter;
  }) || [];

  return (
    <div className={styles["history-tab"]}>
      <Toolbar
        search={search}
        onSearch={setSearch}
        activeFilter={activeFilter}
        onFilter={setActiveFilter}
      />
      <div className={styles["history-tab__list"]}>
        {filtered.map((c) => (
          <CampaignCard key={c.id} campaign={c} />
        ))}
        {filtered.length === 0 && (
          <p
            style={{
              textAlign: "center",
            }}
          >
            No campaigns found.
          </p>
        )}
      </div>
    </div>
  );
};

export default EmailHistoryTab;
